import React, { useState } from 'react';
import {
    Box,
    Avatar,
    Button,
    Typography,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { useSnackbar } from 'notistack';
import { HttpService } from 'core/services/http';
import useProfilePhotoCropFlow from '../../hooks/useProfilePhotoCropFlow';
import {
    getProfileAvatarUrl,
    getProfileDisplayName,
    getProfileAvatarLetter,
    nameToAvatarColor,
} from 'features/feature_job/utils/profileDisplay';

const ChangeProfilePhoto = ({ profile, onUpdate, open, onClose }) => {
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const { enqueueSnackbar } = useSnackbar();
    const { fileInputRef, handleFileChange, cropDialog } = useProfilePhotoCropFlow({
        profile,
        onUploaded: () => {
            if (onUpdate) onUpdate();
            setError('');
            onClose();
        },
        enqueueSnackbar,
    });

    const user = HttpService.getUser();

    const handleClose = () => {
        if (!uploading) {
            setError('');
            onClose();
        }
    };

    const handleRemovePhoto = async () => {
        if (!window.confirm('Are you sure you want to remove your profile photo?')) {
            return;
        }

        setUploading(true);
        setError('');

        try {
            // Send remove_image flag to backend along with required fields
            const formData = new FormData();
            formData.append('remove_image', 'true');
            
            // Include required fields from existing profile to avoid validation errors
            if (profile?.first_name) {
                formData.append('first_name', profile.first_name);
            }
            if (profile?.last_name) {
                formData.append('last_name', profile.last_name);
            }
            
            const service = new HttpService();
            const response = await service.post('/auth/profile/update/', formData, true);

            if (response?.error) {
                setError(response.error);
                setUploading(false);
                return;
            }

            enqueueSnackbar('Profile photo removed successfully', { variant: 'success' });
            
            if (onUpdate) {
                onUpdate();
            }
            
            // Close dialog after successful removal
            handleClose();
        } catch (err) {
            console.error('Error removing image:', err);
            const errorMessage = err?.response?.data?.error || err?.message || 'Failed to remove photo. Please try again.';
            setError(errorMessage);
            enqueueSnackbar('Failed to remove profile photo', { variant: 'error' });
        } finally {
            setUploading(false);
        }
    };

    return (
        <>
            {cropDialog}
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight={600}>
                        Change Profile Photo
                    </Typography>
                    <IconButton
                        onClick={handleClose}
                        disabled={uploading}
                        size="small"
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, py: 2 }}>
                    <Box sx={{ position: 'relative' }}>
                        <Avatar
                            src={getProfileAvatarUrl(profile) || undefined}
                            alt={getProfileDisplayName(profile, user)}
                            onClick={() => !uploading && fileInputRef.current?.click()}
                            aria-label="Choose new profile photo"
                            sx={{
                                width: 150,
                                height: 150,
                                border: '3px solid',
                                borderColor: 'divider',
                                fontSize: '3rem',
                                fontWeight: 700,
                                bgcolor: nameToAvatarColor(getProfileDisplayName(profile, user)),
                                color: 'common.white',
                                cursor: uploading ? 'default' : 'pointer',
                                '&:hover': uploading ? {} : { opacity: 0.92 },
                            }}
                        >
                            {getProfileAvatarLetter(profile, user)}
                        </Avatar>
                        {uploading && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: 'rgba(0, 0, 0, 0.5)',
                                    borderRadius: '50%'
                                }}
                            >
                                <CircularProgress size={50} />
                            </Box>
                        )}
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ width: '100%' }}>
                            {error}
                        </Alert>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={handleFileChange}
                            disabled={uploading}
                        />
                        <Button
                            variant="outlined"
                            color="inherit"
                            startIcon={<PhotoCameraIcon />}
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            fullWidth
                            size="large"
                            sx={{
                                borderColor: 'divider',
                                color: 'text.primary',
                                '&:hover': {
                                    borderColor: 'text.secondary',
                                    bgcolor: 'action.hover',
                                },
                            }}
                        >
                            {uploading ? 'Uploading...' : 'Upload New Photo'}
                        </Button>
                        {profile?.image && (
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={handleRemovePhoto}
                                disabled={uploading}
                                fullWidth
                                size="large"
                            >
                                Remove Photo
                            </Button>
                        )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" textAlign="center">
                        Recommended: Square image, at least 400x400 pixels. Max file size: 5MB
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2, pt: 0 }}>
                <Button onClick={handleClose} disabled={uploading}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
};

export default ChangeProfilePhoto;
